import classNames from "classnames";
import { FC, ReactNode } from "react";
import SvgPhotoLibrary from "../icons/PhotoLibrary";
import FormField from "./FormField";
import Icon from "./Icon";
import Input from "./Input";
import styles from "./SelectionConfigurator.module.css";

export type SelectionConfiguratorCard = {
  key: string;
  label: ReactNode;
  title?: string;
  imageSrc?: string;
  fallbackText?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

export type SelectionConfiguratorSelection = {
  title?: ReactNode;
  groupTitle?: ReactNode;
  cards: SelectionConfiguratorCard[];
  emptyText?: ReactNode;
};

export type SelectionConfiguratorSelectionSections = {
  [key: string]: SelectionConfiguratorSelection | null | undefined;
};

export type SelectionConfiguratorSummaryItem = {
  label: string;
  value: ReactNode;
  hidden?: boolean;
};

export type SelectionConfiguratorSummary = {
  title?: ReactNode;
  items: SelectionConfiguratorSummaryItem[];
};

export interface SelectionConfiguratorProps {
  form: ReactNode;
  selection?: SelectionConfiguratorSelection | null;
  selectionSections?: SelectionConfiguratorSelectionSections;
  activeSelectionKey?: string | null;
  selectionDisabled?: boolean;
  summary?: SelectionConfiguratorSummary | null;
  className?: string;
}

export interface SelectionConfiguratorVisualFieldProps {
  children: ReactNode;
  active?: boolean;
  disabled?: boolean;
  onOpenSelection: () => void;
  title?: string;
  ariaLabel?: string;
}

export const SelectionConfiguratorVisualField: FC<SelectionConfiguratorVisualFieldProps> = ({
  children,
  active,
  disabled,
  onOpenSelection,
  title = "Show visual choices",
  ariaLabel = title,
}: SelectionConfiguratorVisualFieldProps) => (
  <div className={styles.visualField}>
    {children}
    <button
      type="button"
      className={classNames(styles.visualFieldButton, {
        [styles.activeVisualFieldButton]: active,
      })}
      disabled={disabled}
      onClick={onOpenSelection}
      title={title}
      aria-label={ariaLabel}
    >
      <Icon glyph={SvgPhotoLibrary} />
    </button>
  </div>
);

const cardFallbackText = (card: SelectionConfiguratorCard) =>
  card.fallbackText ?? (typeof card.label === "string" ? card.label.slice(0, 2) : "-");

const isInputValue = (value: ReactNode): value is string | number =>
  typeof value === "string" || typeof value === "number";

const summaryInputValue = (value: ReactNode): string | number => {
  if (isInputValue(value)) {
    return value;
  }

  if (value == null || typeof value === "boolean") {
    return "";
  }

  return `${value}`;
};

const shouldRenderSummaryInput = (value: ReactNode) =>
  isInputValue(value) || value == null || typeof value === "boolean";

const SelectionConfigurator: FC<SelectionConfiguratorProps> = ({
  form,
  selection,
  selectionSections,
  activeSelectionKey,
  selectionDisabled,
  summary,
  className,
}: SelectionConfiguratorProps) => {
  const resolvedSelection =
    selection ?? (activeSelectionKey ? (selectionSections?.[activeSelectionKey] ?? null) : null);

  return (
    <div className={classNames(styles.layout, className)}>
      <div className={styles.form}>{form}</div>

      <div className={styles.selection}>
        {resolvedSelection && (
          <>
            {!!resolvedSelection.title && <h3 className={styles.columnTitle}>{resolvedSelection.title}</h3>}
            <div className={styles.selectionGroup}>
              {!!resolvedSelection.groupTitle && (
                <div className={styles.selectionGroupTitle}>{resolvedSelection.groupTitle}</div>
              )}
              {resolvedSelection.cards.length ? (
                <div className={styles.selectionCards}>
                  {resolvedSelection.cards.map((card) => {
                    const isCardDisabled = selectionDisabled || card.disabled;

                    return (
                      <button
                        key={card.key}
                        type="button"
                        className={classNames(styles.selectionCard, {
                          [styles.selectedSelectionCard]: card.selected,
                        })}
                        disabled={isCardDisabled}
                        onClick={card.onClick}
                        title={`${card.label}`}
                        aria-pressed={card.selected}
                      >
                        <div className={styles.selectionCardImage}>
                          {card.imageSrc ? <img src={card.imageSrc} alt="" /> : <span>{cardFallbackText(card)}</span>}
                        </div>
                        <span className={styles.selectionCardLabel}>{card.label}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.emptySelection}>{resolvedSelection.emptyText}</div>
              )}
            </div>
          </>
        )}
      </div>

      <div className={styles.summary}>
        {summary && (
          <>
            {!!summary.title && <h3 className={styles.columnTitle}>{summary.title}</h3>}
            {summary.items.map((item, index) =>
              item.hidden ? null : (
                <FormField key={`${item.label}-${index}`} label={item.label}>
                  {({ labelId }) =>
                    shouldRenderSummaryInput(item.value) ? (
                      <Input id={labelId} value={summaryInputValue(item.value)} disabled />
                    ) : (
                      <div id={labelId} className={styles.summaryValue}>
                        {item.value}
                      </div>
                    )
                  }
                </FormField>
              ),
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SelectionConfigurator;
